import { Badge, Button, Card, Checkbox, EmptyState, Modal, Spinner, useToast } from '@ohear/ui'
import { createFileRoute } from '@tanstack/react-router'
import { Suspense, lazy, useMemo, useState } from 'react'
import { useBulkApproveTakes, useReviewQueue } from '../../../lib/api/supervisor'
import type { PendingTake } from '../../../lib/api/supervisor'
import { useRequireRole } from '../../../lib/auth'
import { useRealtimeChannel } from '../../../lib/realtime/use-channel'

// Heavy supervisor surface (audio playback, QC tables, pickup form) is
// `React.lazy`-imported so it lands in the `feature-supervisor` chunk and is
// only fetched once a supervisor opens the queue.
const TakeReviewer = lazy(() =>
  import('../../../features/supervisor/TakeReviewer').then((m) => ({
    default: m.TakeReviewer,
  })),
)
const PickupRequestModal = lazy(() =>
  import('../../../features/supervisor/PickupRequestModal').then((m) => ({
    default: m.PickupRequestModal,
  })),
)

export const Route = createFileRoute('/supervisor/projects/$projectId/takes')({
  component: SupervisorTakesQueue,
})

function FeatureFallback({ label }: { label: string }) {
  return (
    <div className="flex justify-center p-12">
      <Spinner size="lg" label={label} />
    </div>
  )
}

function SupervisorTakesQueue() {
  useRequireRole(['supervisor'])
  const { projectId } = Route.useParams()
  const queue = useReviewQueue(projectId)
  const bulkApprove = useBulkApproveTakes(projectId)
  const toast = useToast()

  const [selected, setSelected] = useState<ReadonlySet<string>>(new Set())
  const [expandedId, setExpandedId] = useState<string | null>(null)
  const [modalTake, setModalTake] = useState<PendingTake | null>(null)
  const [pickupTake, setPickupTake] = useState<PendingTake | null>(null)

  // Realtime: invalidate the queue when any take or comment event fires for
  // this org. Both topics are subscribed because supervisors react to take
  // uploads + producer comments on takes they're reviewing.
  const orgId = (queue.data as { organization_id?: string } | undefined)?.organization_id ?? null
  useRealtimeChannel({
    orgId,
    topic: 'takes',
    invalidateKeys: [['supervisor', 'queue', projectId]],
  })
  useRealtimeChannel({
    orgId,
    topic: 'comments',
    invalidateKeys: [['supervisor', 'queue', projectId]],
  })

  const takes = queue.data?.takes ?? []

  const toggle = (id: string) => {
    setSelected((prev) => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })
  }

  const allChecked = useMemo(
    () => takes.length > 0 && takes.every((t) => selected.has(t.take_id)),
    [takes, selected],
  )

  const toggleAll = () => {
    setSelected(allChecked ? new Set() : new Set(takes.map((t) => t.take_id)))
  }

  const submitBulk = () => {
    if (selected.size === 0) return
    bulkApprove.mutate(Array.from(selected), {
      onSuccess: (data) => {
        toast.show({
          severity: 'success',
          title: `${data.approved} takes approved`,
        })
        setSelected(new Set())
      },
      onError: (err) => {
        toast.show({
          severity: 'error',
          title: 'Bulk approve failed',
          description: err instanceof Error ? err.message : 'Unknown error',
        })
      },
    })
  }

  return (
    <section className="flex flex-col gap-4">
      <header className="flex flex-wrap items-end justify-between gap-3 pb-4 border-b border-[var(--border)]">
        <div className="flex flex-col gap-1">
          <div className="text-[12px] uppercase tracking-wide text-[var(--text-3)]">Supervisor</div>
          <h1 className="text-[22px] font-semibold tracking-tight m-0">Review queue</h1>
          <p className="text-[13px] text-[var(--text-2)] m-0">
            {queue.data ? (
              <>
                <span className="tabular-nums font-medium text-[var(--text)]">
                  {queue.data.total_pending}
                </span>{' '}
                take{queue.data.total_pending === 1 ? '' : 's'} pending
              </>
            ) : (
              'Loading…'
            )}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <span className="text-[13px] text-[var(--text-2)] tabular-nums">
            {selected.size} selected
          </span>
          <Button
            variant="primary"
            disabled={selected.size === 0}
            loading={bulkApprove.isPending}
            onClick={submitBulk}
          >
            Bulk approve ({selected.size})
          </Button>
        </div>
      </header>

      {queue.isLoading ? (
        <div className="flex items-center gap-2 text-[var(--text-2)]">
          <Spinner /> Loading queue...
        </div>
      ) : null}

      {queue.error ? (
        <Card>
          <p className="m-0 text-[var(--red)]">
            Couldn't load queue.{' '}
            {queue.error instanceof Error ? queue.error.message : 'Unknown error'}
          </p>
        </Card>
      ) : null}

      {!queue.isLoading && takes.length === 0 ? (
        <EmptyState title="Inbox zero" description="No takes are waiting on you. Nice." />
      ) : null}

      {takes.length > 0 ? (
        <Card dense data-tour="take-queue">
          <div className="flex items-center gap-2 px-1 pb-2 border-b border-[var(--border)]">
            <Checkbox checked={allChecked} onChange={toggleAll} aria-label="Select all takes" />
            <span className="text-[12px] text-[var(--text-2)]">Select all</span>
          </div>
          <ul className="m-0 p-0 list-none">
            {takes.map((t) => (
              <li key={t.take_id} className="border-b border-[var(--border)] last:border-b-0">
                <TakeRow
                  take={t}
                  selected={selected.has(t.take_id)}
                  onSelectToggle={() => toggle(t.take_id)}
                  expanded={expandedId === t.take_id}
                  onExpandToggle={() =>
                    setExpandedId((cur) => (cur === t.take_id ? null : t.take_id))
                  }
                  onOpenModal={() => setModalTake(t)}
                />
                {expandedId === t.take_id ? (
                  <div className="px-4 py-4 bg-[var(--bg-3)]">
                    <Suspense fallback={<FeatureFallback label="Loading reviewer…" />}>
                      <TakeReviewer
                        projectId={projectId}
                        take={t}
                        onDecided={() => setExpandedId(null)}
                        onRequestPickup={(take) => setPickupTake(take)}
                      />
                    </Suspense>
                  </div>
                ) : null}
              </li>
            ))}
          </ul>
        </Card>
      ) : null}

      <Modal
        open={Boolean(modalTake)}
        onClose={() => setModalTake(null)}
        title={
          modalTake ? `Cue ${modalTake.cue_number} - ${modalTake.character_display_name}` : 'Take'
        }
        size="lg"
      >
        {modalTake ? (
          <Suspense fallback={<FeatureFallback label="Loading reviewer…" />}>
            <TakeReviewer
              projectId={projectId}
              take={modalTake}
              onDecided={() => setModalTake(null)}
              onRequestPickup={(take) => {
                setModalTake(null)
                setPickupTake(take)
              }}
            />
          </Suspense>
        ) : null}
      </Modal>

      {pickupTake ? (
        <Suspense fallback={null}>
          <PickupRequestModal
            projectId={projectId}
            take={pickupTake}
            onClose={() => setPickupTake(null)}
          />
        </Suspense>
      ) : null}
    </section>
  )
}

function TakeRow({
  take,
  selected,
  onSelectToggle,
  expanded,
  onExpandToggle,
  onOpenModal,
}: {
  take: PendingTake
  selected: boolean
  onSelectToggle: () => void
  expanded: boolean
  onExpandToggle: () => void
  onOpenModal: () => void
}) {
  const failCount = take.qc.filter((q) => q.severity === 'fail').length
  const warnCount = take.qc.filter((q) => q.severity === 'warn').length
  return (
    <div className="flex items-center gap-2 px-1 py-2 text-[13px]">
      <Checkbox
        checked={selected}
        onChange={onSelectToggle}
        aria-label={`Select cue ${take.cue_number}`}
      />
      <Waveform pathD={take.waveform_path_d} viewBoxWidth={take.waveform_path_width} />
      <button
        type="button"
        onClick={onExpandToggle}
        aria-expanded={expanded}
        className="text-left flex-1 min-w-0 outline-none focus-visible:shadow-[var(--focus-ring)] rounded-[var(--radius-sm)] px-1"
      >
        <div className="flex items-center gap-2 flex-wrap">
          <Badge variant="gray">{take.character_code}</Badge>
          <span className="font-medium truncate">{take.character_display_name}</span>
          <span className="text-[var(--text-2)]">cue {take.cue_number}</span>
          <span className="text-[var(--text-2)]">v{take.version}</span>
          {failCount > 0 ? <Badge qcSeverity="fail">{failCount}</Badge> : null}
          {warnCount > 0 ? <Badge qcSeverity="warn">{warnCount}</Badge> : null}
          {failCount === 0 && warnCount === 0 ? <Badge qcSeverity="pass">pass</Badge> : null}
        </div>
        <div className="text-[12px] text-[var(--text-2)] truncate">{take.cue_text}</div>
      </button>
      <span className="text-[12px] text-[var(--text-2)] tabular-nums whitespace-nowrap">
        {take.duration_sec.toFixed(1)}s
      </span>
      <Button size="sm" variant="ghost" onClick={onOpenModal}>
        Open
      </Button>
    </div>
  )
}

function Waveform({
  pathD,
  viewBoxWidth,
}: {
  pathD: string | null
  viewBoxWidth: number | null
}) {
  if (!pathD) {
    return (
      <span
        aria-hidden="true"
        className="inline-block w-[120px] h-[40px] rounded-[var(--radius-sm)] bg-[var(--bg-3)]"
      />
    )
  }
  const w = viewBoxWidth ?? 240
  return (
    <svg
      role="img"
      aria-label="Waveform"
      viewBox={`0 0 ${w} 80`}
      width={120}
      height={40}
      preserveAspectRatio="none"
      className="rounded-[var(--radius-sm)] bg-[var(--bg-3)]"
    >
      <path d={pathD} fill="none" stroke="currentColor" strokeWidth={1.5} />
    </svg>
  )
}
